import type { GroupRole } from './group-access';
import type { StrongerMemoryGroup } from './sub-groups';

export type GroupDashboardAvatar =
  | {
      kind: 'fixture';
      name: 'sunrise' | 'bob' | 'tina' | 'margaret' | 'face1' | 'face2' | 'face3' | 'face4';
    }
  | { kind: 'remote'; url: string };

export interface GroupDashboardMember {
  id: string;
  name: string;
  role?: GroupRole;
  avatar?: GroupDashboardAvatar;
  done: number;
  total: number;
}

/** Optional dashboard data, separate from membership and its authorization. */
export interface GroupDashboardDetails {
  cardVariant?: 'standard' | 'compact';
  leaderName?: string;
  /** The viewer leads the group: "Led by you" rather than their own name. */
  leaderIsYou?: boolean;
  avatar?: GroupDashboardAvatar;
  avatarTone: 'orange' | 'olive';
  memberCount?: number;
  role?: GroupRole;
  today: { done: number; total: number; energy: 'great' | 'early' | 'complete' };
  /** Explicit history evidence; a missing recent message alone does not mean a new chat. */
  hasMessages?: boolean;
  latestMessage?: { senderName: string; text: string; avatar?: GroupDashboardAvatar };
  /** Unknown without a chat source; the chat summary fills it in then. */
  unreadCount?: number;
  overview: {
    completed: number;
    total: number;
    streak: number;
    month: number;
    faces: Array<{ avatar?: GroupDashboardAvatar; label: string }>;
    overflow: number;
  };
  weekStart: string;
  weekAverages: number[];
  exerciseCompletion: { reading: number; writing: number; math: number };
  members: GroupDashboardMember[];
}

export type GroupDashboardDetailsStatus = 'loading' | 'available' | 'unavailable' | 'error';
export interface GroupDashboard extends StrongerMemoryGroup {
  details?: GroupDashboardDetails;
  /** Optional for existing adapters; missing status is inferred from the presence of details. */
  detailsStatus?: GroupDashboardDetailsStatus;
  isPreview: boolean;
}

/** A future API adapter may supply group-scoped details after membership has loaded. */
export interface GroupDashboardSource {
  getDetails(groupId: string): Promise<GroupDashboardDetails | undefined>;
}

export function resolveGroupDashboard(
  group: StrongerMemoryGroup,
  details?: GroupDashboardDetails,
  isPreview = false,
  detailsStatus: GroupDashboardDetailsStatus = details ? 'available' : 'unavailable',
): GroupDashboard {
  // A display role must never grant or contradict the live membership's management access.
  const role = details?.role;
  const resolvedDetails = details
    ? { ...details, role: !group.manages && role !== 'member' ? undefined : role }
    : undefined;
  return {
    ...group,
    details: resolvedDetails,
    detailsStatus,
    isPreview: !!resolvedDetails && isPreview,
  };
}

/**
 * The person's role badge on a Groups tab card. Managing a group doesn't say whether they lead
 * it, facilitate it or only hold a parent group's admin rights, so only echo-api's details name a
 * manager's role.
 */
export function dashboardRole(
  group: Pick<GroupDashboard, 'manages' | 'details'>,
): GroupRole | undefined {
  return group.details?.role ?? (group.manages ? undefined : 'member');
}

/** Fictional content for explicitly enabled local previews; creates no memberships. */
export function createMockGroupDashboardDetails(index: number): GroupDashboardDetails {
  const position = ((index % 3) + 3) % 3;
  const leaderName = ['Maria T.', 'Bob S.', 'Priya N.'][position] ?? 'Maria T.';
  const todayStates = [
    { done: 0, total: 3, energy: 'great' },
    { done: 16, total: 18, energy: 'early' },
    { done: 18, total: 18, energy: 'complete' },
  ] as const;
  return {
    cardVariant: position === 0 ? 'standard' : 'compact',
    leaderName,
    avatar: position === 0 ? { kind: 'fixture', name: 'sunrise' } : undefined,
    avatarTone: position === 2 ? 'olive' : 'orange',
    memberCount: position === 0 ? 24 : 18,
    today: todayStates[position] ?? todayStates[0],
    hasMessages: position !== 1,
    latestMessage:
      position === 0
        ? {
            senderName: 'Bob M.',
            text: "I'll be there tomorrow!",
            avatar: { kind: 'fixture', name: 'bob' },
          }
        : position === 2
          ? {
              senderName: 'Tina Z.',
              text: 'Well done everyone we did it!',
              avatar: { kind: 'fixture', name: 'tina' },
            }
          : undefined,
    unreadCount: [2, 0, 1][position] ?? 2,
    overview: {
      completed: 20,
      total: 22,
      streak: 12,
      month: 18,
      faces: [
        { avatar: { kind: 'fixture', name: 'face1' }, label: 'Susan B.' },
        { avatar: { kind: 'fixture', name: 'face2' }, label: 'Margaret A.' },
        { avatar: { kind: 'fixture', name: 'face3' }, label: 'David W.' },
        { avatar: { kind: 'fixture', name: 'face4' }, label: 'Robert H.' },
      ],
      overflow: 3,
    },
    weekStart: '2025-06-09',
    weekAverages: [2, 3, 3, 2, 3, 3, 1],
    exerciseCompletion: { reading: 0.94, writing: 0.88, math: 0.76 },
    members: [
      { id: 'preview-susan', name: 'Susan B.', role: 'leader', done: 3, total: 3 },
      {
        id: 'preview-margaret',
        name: 'Margaret A.',
        avatar: { kind: 'fixture', name: 'margaret' },
        done: 3,
        total: 3,
      },
      { id: 'preview-david', name: 'David W.', done: 3, total: 3 },
      { id: 'preview-robert', name: 'Robert H.', done: 2, total: 3 },
      { id: 'preview-patricia', name: 'Patricia L.', done: 2, total: 3 },
    ],
  };
}
